import React, { useCallback, useReducer } from 'react';
import classNames from 'classnames';
import { TextInput, Clickable } from '@moda/om';
import ChevronRightIcon from '@moda/icons/chevron-right-20';
import { reportError } from '../../lib/reportError';
import { setNewsletterPreference } from '../../lib/setNewsletterPreference';
import { Gender } from '../../constants/gender';
import { Link } from '../Link';

import './SiteFooterMailingList.scss';

interface State {
  successful: boolean;
  error: string | null;
  user: {
    email: string | null;
    gender: Gender;
  };
}

type Action =
  | { type: 'UPDATE_USER'; payload: { email?: string; gender?: Gender } }
  | { type: 'ERROR'; payload: { error: string } }
  | { type: 'SUCCESS' };

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'UPDATE_USER':
      return { ...state, user: { ...state.user, ...action.payload }, successful: false };
    case 'ERROR':
      return { ...state, error: action.payload.error };
    case 'SUCCESS':
      return { ...state, successful: true, error: null, user: { ...state.user, email: null } };
  }
};

export const SiteFooterMailingList: React.FC = () => {
  const [state, dispatch] = useReducer(reducer, {
    successful: false,
    error: null,
    user: {
      email: null,
      gender: 'women'
    }
  });

  const onSuccess = useCallback(() => dispatch({ type: 'SUCCESS' }), []);
  const onFail = useCallback(err => {
    dispatch({
      type: 'ERROR',
      payload: { error: 'Unable to subscribe you at this time' }
    });
    reportError(err);
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setNewsletterPreference({ user: state.user, onSuccess, onFail });
    },
    [onSuccess, onFail, state.user]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      dispatch({ type: 'UPDATE_USER', payload: { email: event.currentTarget.value } }),
    []
  );

  return (
    <form className="SiteFooterMailingList" onSubmit={handleSubmit} action="/ooo">
      <div className="SiteFooterMailingList__input">
        <TextInput
          required
          type="email"
          value={state.user.email || ''}
          placeholder={state.successful ? 'Email submitted!' : 'Sign up for our newsletter'}
          onChange={handleChange}
          error={!!state.error}
        />

        <Clickable
          aria-label="Submit email for our newsletter"
          className="SiteFooterMailingList__button"
          type="submit"
        >
          <ChevronRightIcon />
        </Clickable>
      </div>

      {state.error && (
        <div
          className={classNames('SiteFooterMailingList__status', {
            'SiteFooterMailingList__status--error': state.error
          })}
        >
          {state.error}
        </div>
      )}

      <div className="SiteFooterMailingList__opt-out">
        To opt-out of email sharing or to view our Privacy Policy,{' '}
        <Link className="SiteFooterMailingList__link" to="/privacy">
          click here
        </Link>
      </div>
    </form>
  );
};
